import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';

export const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

export const DialogContainer = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden; /* keeps the full-bleed divider inside the rounded corners */
`;

export const DialogBody = styled.div`
  padding: 24px 24px 0 24px;
`;

export const DialogFooter = styled.div`
  padding: 0 24px 24px 24px;
`;

export const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 32px;
`;

export const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const StepCircle = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  position: relative;
  flex-shrink: 0;
`;

export const StepErrorBadge = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  z-index: 1;
`;

export const StepLabel = styled.div<{ status: string }>`
  font-family: 'Averta Std', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    props.status === 'notdone'
      ? 'var(--color-text-secondary)'
      : 'var(--color-i-prime-text-dark)'};
`;

/* --- Line between step icons: solid for reached steps, half-solid /
   half-dashed for the segment departing the current step towards the next
   (not yet reached) one, and solid light gray for fully future segments. --- */
export const StepConnector = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  margin: 0 8px;
  min-width: 24px;
`;

export const ConnectorHalf = styled.div<{ dashed: boolean; color: string }>`
  flex: 1;
  height: 0;
  border-top: 2px ${(props) => (props.dashed ? 'dashed' : 'solid')}
    ${(props) => props.color};
`;

export const ContentArea = styled.div`
  min-height: 300px;
`;

export const Title = styled.h2`
  margin-bottom: ${Spacings.SPACING_6};
  font-size: ${Fonts.H3};
  font-weight: ${Fonts.HEAVY};
  line-height: ${Spacings.SPACING_8B};
  color: var(--color-text-primary);
`;

export const Description = styled.p`
  font-size: ${Fonts.H5};
  font-weight: ${Fonts.LIGHT};
  line-height: ${Spacings.SPACING_6B};
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_13};
`;

export const ButtonDivider = styled.div`
  height: 1px;
  width: 100%;
  background: var(--color-border, #e0e0e0);
  margin-bottom: ${Spacings.SPACING_6B};
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
`;

export const Button = styled.button`
  flex: 1;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PrimaryButton = styled(Button)`
  color: white;
  background: var(--color-base-fill);
`;

export const SecondaryButton = styled(Button)`
  background: transparent;
  border: 1px solid var(--color-base-fill);

  &:hover:not(:disabled) {
    background: #e3f2fd;
  }
`;

export const ErrorMessage = styled.div`
  background: #ffebee;
  color: var(--color-i-red-5);
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-line; /* Preserve line breaks from \n */

  strong {
    display: block;
    margin-bottom: 8px;
  }
`;

/* --- Small "enable in browser" message shown below the image/preview
   when the user dismisses the native permission prompt with the X.
   Reused for both camera and mic. --- */
export const PermissionEnableMessageWrapper = styled.div`
  width: 452px;
  max-width: 100%;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 1;
  border-radius: 8px;
  padding: 8px;
  background: #fef5f5;
  margin-bottom: 16px;
`;

export const PermissionEnableMessageText = styled.p`
  margin: 0;
  opacity: 1;
  font-family: 'Averta Std', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0%;
  text-align: center;
  vertical-align: middle;
  color: var(--color-i-red);
`;

/* --- Mic "how to allow" options, shown below the mic image whenever
   permission isn't permanently blocked (i.e. still an actionable prompt). --- */
export const MicOptionsList = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const MicOptionRow = styled.p`
  margin: 0;
  font-family: 'Averta Std', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0%;
  color: var(--color-text-primary);
`;

export const MicOptionLabel = styled.span`
  font-family: 'Averta Std', sans-serif;
  font-weight: 600;
  font-style: normal;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.5%;
  vertical-align: baseline;
  color: var(--color-text-primary);
`;

/* --- Screen-share "how to share" steps, shown below the screen-share image --- */
export const ScreenShareStepsList = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

export const ScreenShareStepRow = styled.p`
  margin: 0;
  text-align: left;
  font-family: 'Averta Std', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0%;
  color: var(--color-text-primary);
`;

export const ScreenShareStepHighlight = styled.span`
  font-family: 'Averta Std', sans-serif;
  font-weight: 600;
  font-style: normal;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.5%;
  vertical-align: baseline;
  color: var(--color-text-primary);
`;
